import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../../api/client';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ChevronRight, ChevronDown, Check } from 'lucide-react';

/**
 * Member Insights — Phase 2 UI
 * 리스트형 Accordion. 공간 효율 우선.
 */
const DEFAULT_LIMIT = 30;

function formatKstDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  // KST
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const y = kst.getUTCFullYear();
  const m = String(kst.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(kst.getUTCDate()).padStart(2, '0');
  return `${y}.${m}.${dd}`;
}

function groupByDate(insights) {
  const map = new Map();
  for (const ins of insights) {
    const key = formatKstDate(ins.measured_at || ins.created_at);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(ins);
  }
  return Array.from(map.entries()); // [ [date, [insights...]], ... ]
}

const CATEGORY_KO = {
  strength: '근력',
  muscular_endurance: '근지구력',
  endurance: '근지구력',
  cardio: '심폐지구력',
  inbody: '체성분',
  body_composition: '체성분',
  posture: '체형',
  body_balance: '체형',
};

const EXERCISE_KO = {
  squat: '스쿼트',
  deadlift: '데드리프트',
  bench_press: '벤치프레스',
  shoulder_press: '숄더프레스',
  pushup: '푸시업',
  push_up: '푸시업',
  situp: '싯업',
  sit_up: '싯업',
  plank: '플랭크',
  wall_squat: '월스쿼트',
  running: '러닝',
  rowing: '로잉',
  barbell_squat_endurance: '바벨 스쿼트(지구력)',
  barbell_deadlift_endurance: '바벨 데드리프트(지구력)',
  barbell_shoulder_press_endurance: '바벨 숄더프레스(지구력)',
  amrap1: 'AMRAP1',
  amrap2: 'AMRAP2',
  amrap3: 'AMRAP3',
  amrap4: 'AMRAP4',
};

const koCategory = (slug) => (slug ? CATEGORY_KO[slug] || slug : '');
const koExercise = (slug) => (slug ? EXERCISE_KO[slug] || slug : '');

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

function formatSign(delta) {
  if (delta == null) return { text: '', className: '' };
  const n = Number(delta);
  if (Number.isNaN(n)) return { text: '', className: '' };
  const rounded = Math.round(n * 10) / 10;
  if (rounded > 0) return { text: `(+${rounded}점)`, className: 'text-green-600 font-medium' };
  if (rounded < 0) return { text: `(${rounded}점)`, className: 'text-red-600 font-medium' };
  return { text: '(0점)', className: 'text-gray-500' };
}

function formatSeconds(totalSec) {
  if (totalSec == null) return '';
  const s = Math.round(Number(totalSec));
  if (Number.isNaN(s)) return '';
  if (s < 60) return `${s}초`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}분` : `${m}분 ${r}초`;
}

function raw(m, exerciseSlug) {
  if (!m || typeof m !== 'object') return '';
  // 무게 × 횟수 (근력)
  if (m.weight != null && m.reps != null) return `${m.weight}kg × ${m.reps}회`;
  if (m.weight != null) return `${m.weight}kg`;
  // 시간 기반 (plank, wall_squat)
  if (m.duration_s != null && m.distance_m == null && m.reps == null) {
    return formatSeconds(m.duration_s);
  }
  // 심폐 - 거리 + 시간
  if (m.distance_m != null && m.duration_s != null) {
    return `${m.distance_m}m / ${formatSeconds(m.duration_s)}`;
  }
  if (m.distance_m != null) return `${m.distance_m}m`;
  // 반복 횟수만 (pushup, situp)
  if (m.reps != null) return `${m.reps}회`;
  // AMRAP
  if (m.rounds != null || m.partial_reps != null) {
    const rounds = m.rounds != null ? `${m.rounds}R` : '';
    const partial = m.partial_reps != null ? ` +${m.partial_reps}` : '';
    return `${rounds}${partial}`.trim();
  }
  return '';
}

const MemberInsights = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [memberName, setMemberName] = useState('');
  const [sort, setSort] = useState('newest');
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState(null);

  // 초기 로드 시: 오늘 KST 자정 기준 end_date 세팅, start_date는 비워 두면 서버가 전체 반환
  useEffect(() => {
    const now = new Date();
    const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    const y = kst.getUTCFullYear();
    const m = String(kst.getUTCMonth() + 1).padStart(2, '0');
    const d = String(kst.getUTCDate()).padStart(2, '0');
    setEndDate(`${y}-${m}-${d}`);
  }, []);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const params = { sort, limit: DEFAULT_LIMIT, offset };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (memberName.trim()) params.member_name = memberName.trim();
      const res = await apiClient.get('/admin/member-insights', { params });
      setInsights(res.data.insights || []);
      setTotal(res.data.total || 0);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Insight 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInsights(); }, [sort, offset]);  // filter change 시 applyFilters로 명시 트리거

  const applyFilters = () => {
    setOffset(0);
    fetchInsights();
  };

  const groups = useMemo(() => groupByDate(insights), [insights]);

  const toggleCheck = async (ins, e) => {
    e?.stopPropagation();
    try {
      const next = !ins.is_checked;
      await apiClient.patch(`/admin/member-insights/${ins._id}/status`, { is_checked: next });
      setInsights((prev) =>
        prev.map((x) => (x._id === ins._id ? { ...x, is_checked: next, status: next ? 'read' : 'unread' } : x))
      );
      setUnreadCount((c) => Math.max(0, c + (next ? -1 : 1)));
      toast.success(next ? '확인 처리했습니다' : '미확인으로 되돌렸습니다');
    } catch (err) {
      toast.error('상태 변경 실패');
    }
  };

  const goToFitnessScore = (memberId) => {
    navigate(`/admin/members/${memberId}?tab=fitness-score`);
  };

  const canPrev = offset > 0;
  const canNext = offset + DEFAULT_LIMIT < total;

  return (
    <div className="p-6 space-y-4" data-testid="member-insights-page">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Member Insights</h1>
        <div className="text-sm text-gray-500 space-x-4" data-testid="insights-summary">
          <span>전체 <b className="text-gray-900">{total.toLocaleString()}</b>건</span>
          <span>미확인 <b className="text-orange-600">{unreadCount.toLocaleString()}</b>건</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <div>
          <Label className="text-xs text-gray-500">시작일</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 text-sm" data-testid="insights-start-date" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">종료일</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9 text-sm" data-testid="insights-end-date" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">상태</Label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="insights-status-filter">
            <option value="all">전체</option>
            <option value="unchecked">미확인</option>
            <option value="checked">확인</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">회원명</Label>
          <Input value={memberName} onChange={(e) => setMemberName(e.target.value)} placeholder="검색" className="h-9 text-sm" data-testid="insights-member-search" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">정렬</Label>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="insights-sort">
            <option value="newest">최신순</option>
            <option value="oldest">오래된순</option>
          </select>
        </div>
        <div>
          <Button onClick={applyFilters} className="h-9 w-full bg-gray-900 hover:bg-gray-800" data-testid="insights-apply-filters">
            검색
          </Button>
        </div>
      </div>

      {/* List grouped by date */}
      <div className="space-y-3" data-testid="insights-list">
        {loading ? (
          <div className="text-center py-8 text-sm text-gray-500">불러오는 중...</div>
        ) : groups.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500" data-testid="insights-empty">
            표시할 Insight가 없습니다.
          </div>
        ) : (
          groups.map(([date, items]) => (
            <div key={date} className="border rounded-lg bg-white">
              <div className="px-4 py-2 text-xs font-medium text-gray-500 border-b bg-gray-50" data-testid={`insights-group-${date}`}>
                {(() => {
                  // date is 'YYYY.MM.DD' — reconstruct KST midnight to compute weekday
                  const [y, m, d] = date.split('.').map((n) => parseInt(n, 10));
                  if (!y || !m || !d) return date;
                  // Construct at KST midnight
                  const dt = new Date(Date.UTC(y, m - 1, d) - 9 * 60 * 60 * 1000);
                  const kst = new Date(dt.getTime() + 9 * 60 * 60 * 1000);
                  return `${date} (${WEEKDAY_KO[kst.getUTCDay()]})`;
                })()}
              </div>
              <ul className="divide-y">
                {items.map((ins) => {
                  const isOpen = expandedId === ins._id;
                  const memberName = ins.member?.name || '(회원 없음)';
                  return (
                    <li key={ins._id} data-testid={`insight-row-${ins._id}`}>
                      <button
                        type="button"
                        onClick={() => setExpandedId(isOpen ? null : ins._id)}
                        className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 text-left"
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        <span className="font-medium text-sm w-24 shrink-0 truncate">{memberName}</span>
                        <span className="flex-1 text-sm text-gray-800 truncate" data-testid={`insight-headline-${ins._id}`}>
                          {ins.headline}
                        </span>
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded shrink-0 ${
                            ins.is_checked
                              ? 'bg-gray-100 text-gray-500'
                              : 'bg-orange-100 text-orange-700'
                          }`}
                          data-testid={`insight-badge-${ins._id}`}
                        >
                          {ins.is_checked ? '확인' : '미확인'}
                        </span>
                      </button>

                      {/* Accordion body */}
                      {isOpen && (
                        <div className="px-11 pb-4 pt-3 text-sm bg-white space-y-3.5" data-testid={`insight-body-${ins._id}`}>
                          {/* 대표 Insight (Headline) 재노출 */}
                          <div className="text-base font-semibold text-gray-900" data-testid={`insight-body-headline-${ins._id}`}>
                            {ins.headline}
                          </div>
                          <div className="border-t border-gray-100" />

                          {/* 관련 변화 */}
                          <div>
                            <div className="text-xs text-gray-500 mb-1.5">관련 변화</div>
                            <div className="space-y-1">
                              {(ins.all_category_changes || []).map((c, i) => {
                                const sig = formatSign(c.delta);
                                return (
                                <div key={`cat-${i}`} className="text-sm">
                                  <span className="text-gray-700">{koCategory(c.category)}</span>
                                  <span className="text-gray-900 ml-2">
                                    {c.prev} → {c.curr} <span className={sig.className}>{sig.text}</span>
                                  </span>
                                  {c.prev_level && c.curr_level && c.prev_level !== c.curr_level && (
                                    <span className="ml-3 text-xs text-orange-700">
                                      Level {c.prev_level.split(' ')[1]} → {c.curr_level.split(' ')[1]}
                                    </span>
                                  )}
                                </div>
                                );
                              })}
                              {(ins.all_exercise_changes || []).map((e, i) => {
                                const sig = formatSign(e.delta);
                                return (
                                <div key={`ex-${i}`} className="text-sm">
                                  <span className="text-gray-700">{koExercise(e.exercise)}</span>
                                  <span className="text-gray-900 ml-2">
                                    {raw(e.prev_raw_metrics) || e.prev_score}
                                    {' → '}
                                    {raw(e.curr_raw_metrics) || e.curr_score}
                                    <span className={`ml-1 ${sig.className}`}>{sig.text}</span>
                                  </span>
                                </div>
                                );
                              })}
                              {(ins.all_category_changes || []).length === 0 &&
                                (ins.all_exercise_changes || []).length === 0 && (
                                  <div className="text-xs text-gray-500">변화 정보가 없습니다.</div>
                                )}
                            </div>
                          </div>

                          {/* 측정일 (Exercise 이전/최근 측정일이 있는 경우 별도 섹션) */}
                          {(ins.all_exercise_changes || []).some((e) => e.prev_measured_at && e.curr_measured_at) && (
                            <>
                              <div className="border-t border-gray-100" />
                              <div>
                                <div className="text-xs text-gray-500 mb-1.5">측정일</div>
                                <div className="space-y-0.5">
                                  {(ins.all_exercise_changes || [])
                                    .filter((e) => e.prev_measured_at && e.curr_measured_at)
                                    .map((e, i) => (
                                      <div key={`exd-${i}`} className="text-sm text-gray-700">
                                        <span className="text-gray-500 mr-2">{koExercise(e.exercise)}</span>
                                        {formatKstDate(e.prev_measured_at)}
                                        <span className="mx-1.5 text-gray-400">→</span>
                                        {formatKstDate(e.curr_measured_at)}
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </>
                          )}

                          {/* 생성일 */}
                          <div className="border-t border-gray-100" />
                          <div className="text-xs text-gray-500 space-x-4">
                            <span>측정일 {formatKstDate(ins.measured_at)}</span>
                            <span>생성일 {formatKstDate(ins.created_at)}</span>
                          </div>

                          {/* 우측 정렬 버튼 */}
                          <div className="flex items-center justify-end gap-2 pt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => goToFitnessScore(ins.member_id)}
                              data-testid={`insight-goto-fitness-${ins._id}`}
                            >
                              회원 Fitness Score 보기
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => toggleCheck(ins, e)}
                              className={ins.is_checked ? 'text-gray-500 border-gray-300' : 'text-orange-700 border-orange-300 hover:bg-orange-50'}
                              data-testid={`insight-toggle-check-${ins._id}`}
                            >
                              <Check className="h-3.5 w-3.5 mr-1" />
                              {ins.is_checked ? '미확인 처리' : '확인'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm">
        <div className="text-gray-500">
          {total > 0
            ? `${offset + 1}-${Math.min(offset + DEFAULT_LIMIT, total)} / ${total}`
            : ''}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setOffset(Math.max(0, offset - DEFAULT_LIMIT))} disabled={!canPrev} data-testid="insights-prev">
            이전
          </Button>
          <Button variant="outline" size="sm" onClick={() => setOffset(offset + DEFAULT_LIMIT)} disabled={!canNext} data-testid="insights-next">
            다음
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MemberInsights;
